import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Dexie from 'dexie';
import * as uuid from 'uuid';
import { Database } from './DexieDB/class/Database';

import { EncontrarEmOutraTabela } from './functions/EncontrarEmOutraTabela';
import { BuscarLocalmente } from './functions/BuscarLocalmente';
import { FiltrosValidosCriadosOffline } from './functions/FiltrosValidosCriadosOffline';
import { isAnimal } from './functions/IsAnimal';
import { VerificarDadosDoAnimal } from './functions/VerificarDadosDoAnimal';
import { SepararIdUrl } from './functions/SepararIdUrl';
import { Pagination } from './functions/Pagination';

import { IPropriedade } from './DexieDB/mappers/IPropriedade';
import { IAnimal } from './DexieDB/modules/animals/entities/IAnimal';
import { IRegistro } from './DexieDB/mappers/IRegistro';
import { IToPerform } from './DexieDB/modules/toPerform/entities/IToPerform';
import { IUsuario } from './DexieDB/mappers/IUsuario';
import { IFiltrosValidos } from './DexieDB/mappers/IFiltrosValidos';

type DataType =
  | 'propriedades'
  | 'animais'
  | 'animal'
  | 'usuarios'
  | 'registros'
  | 'registro'
  | 'filtrosValidos';

export type ITable = {
  propriedades: Dexie.Table<IPropriedade, any>;
  animais: Dexie.Table<IAnimal, any>;
  registros: Dexie.Table<IRegistro, any>;
  usuarios: Dexie.Table<IUsuario, any>;
  toPerform: Dexie.Table<IToPerform, any>;
  filtrosValidos: Dexie.Table<IFiltrosValidos, any>;
};

export class ApiManager {
  private AxiosInstance: AxiosInstance;

  private DexieInstance: Database;

  private table: ITable;

  constructor(axiosInstance: AxiosInstance, dexieInstance: Database) {
    this.AxiosInstance = axiosInstance;
    this.DexieInstance = dexieInstance;
    this.DexieInstance.open();
    this.table = {
      propriedades: this.DexieInstance.propriedades,
      animais: this.DexieInstance.animais,
      registros: this.DexieInstance.registros,
      usuarios: this.DexieInstance.usuarios,
      toPerform: this.DexieInstance.toPerform,
      filtrosValidos: this.DexieInstance.filtrosValidos,
    };
  }

  public async post(
    url: string,
    data: any,
    connection: boolean,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse | any> {
    if (connection) {
      const response = await this.AxiosInstance.post(url, data, config);
      return response;
    } else {
      if (isAnimal(data)) {
        await VerificarDadosDoAnimal({
          animalTable: this.table.animais,
          animal: data,
        });
        await FiltrosValidosCriadosOffline(data, this.DexieInstance);
      }

      const response = {
        data: await this.table.toPerform.add({
          id: uuid.v4(),
          method: 'POST',
          url,
          data: { estado: 'ativo', ...data },
          config,
          created_at: new Date(),
          updated_at: new Date(),
        }),
      };

      return response;
    }
  }

  public async minhasPropriedades(
    url: string,
    connection: boolean,
    user: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse | any> {
    if (connection === true) {
      const response = await this.AxiosInstance.get(url, config);

      return response;
    } else {
      const properties = await this.table.propriedades.toArray();

      const myProperties = properties.filter(
        property => property.proprietario_id === user.id,
      );
      const colaborations = properties.filter(
        property => property.proprietario_id !== user.id,
      );

      return {
        data: {
          minhasPropriedades: myProperties,
          colaboracoes: colaborations,
        },
      };
    }
  }

  public async get(
    url: string,
    connection: boolean,
    dataType: DataType,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse | any> {
    if (connection === true) {
      const response = await this.AxiosInstance.get(url, config);

      return response;
    } else {
      if (url.split('/').includes('pesquisas')) {
        const data = await Pagination(this.table, url, dataType, config);

        return { data };
      }
      const data = await BuscarLocalmente(dataType, url, config);
      return { data };
    }
  }

  public async patch(
    url: string,
    data: any,
    connection: boolean,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    if (connection) {
      const response = await this.AxiosInstance.patch(url, data, config);
      return response;
    } else {
      throw new Error('Você não pode realizar essa ação offline');
    }
  }

  public async put(
    url: string,
    data: any,
    connection: boolean,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse | any> {
    if (connection) {
      const id = SepararIdUrl(url);

      const encontrado = await EncontrarEmOutraTabela(this.table, { id });

      const response = await this.AxiosInstance.put(url, data, config);

      const key = encontrado?.key as keyof ITable;

      if (encontrado) {
        Object.assign(encontrado.response, data);

        await this.table[key].put(encontrado.response, data.id);
      }

      return response;
    } else {
      const id = SepararIdUrl(url);

      const dataToPerform: IToPerform = {
        id: uuid.v4(),
        method: 'PUT',
        url,
        data,
        config,
        created_at: new Date(),
        updated_at: new Date(),
      };

      const encontrado = await EncontrarEmOutraTabela(this.table, { id });

      Object.assign(data, { id });

      const key = encontrado?.key as keyof ITable;

      if (encontrado && key === 'toPerform') {
        Object.assign(encontrado.response.data, data);

        await this.table[key].put(encontrado.response, data.id);
      } else if (encontrado && key !== 'toPerform') {
        if (
          isAnimal(encontrado.response) &&
          encontrado.response.estado !== 'ativo'
        ) {
          throw new Error('Você não pode atualizar um animal inativo!');
        }
        Object.assign(encontrado.response, data);

        await this.table[key].put(encontrado.response, data.id);

        const response = {
          data: await this.table.toPerform.add(dataToPerform),
        };

        return response;
      } else {
        const response = {
          data: await this.table.toPerform.add(dataToPerform),
        };

        return response;
      }
    }
  }

  public async delete(
    url: string,
    connection: boolean,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse | any> {
    if (connection) {
      const response = await this.AxiosInstance.delete(url, config);
      return response;
    } else {
      const id = SepararIdUrl(url, config);

      const encontrado = await EncontrarEmOutraTabela(this.table, { id });

      if (encontrado) {
        const key = encontrado.key as keyof ITable;
        await this.table[key].delete(id);

        if (key === 'toPerform') {
          return;
        }
      }

      const response = {
        data: await this.table.toPerform.add({
          id: uuid.v4(),
          method: 'DELETE',
          url,
          data: null,
          config,
          created_at: new Date(),
          updated_at: new Date(),
        }),
      };

      return response;
    }
  }
}

export default ApiManager;
